import { Landmark } from 'lucide-react';

import type { GuideModule } from '../types';

/**
 * `requiredPermission: 'fiscal.config'` matches the "Datos Fiscales y ARCA"
 * and "PV fiscal (ARCA)" cards on the `/settings` hub (both gated by
 * `fiscal.config`, `settings-hub-page.tsx`) — this single module covers both
 * screens. Every quoted label was re-checked against
 * `routes/settings/arca-config-page.tsx` and
 * `routes/settings/fiscal-point-of-sale-config-page.tsx`: tabs "Datos del
 * emisor", "Certificados", "Ambiente", "Probar
 * conexión"; buttons "Generar clave y CSR", "Descargar CSR", "Cargar
 * certificado (.crt)", "Confirmar", "Nuevo punto de venta",
 * "Activar"/"Desactivar"; check results "OK" / "Error" (with the detail
 * next to it) / "No realizado"; the "Pasos para facturar con ARCA" progress
 * card (5 numbered steps, "X de 5 listos", per-step "Continuar").
 */
export const FISCAL_CONFIG_MODULE: GuideModule = {
  id: 'fiscal-config',
  title: 'Configuración fiscal y PV fiscal (ARCA)',
  icon: Landmark,
  requiredPermission: 'fiscal.config',
  priority: 11,
  summary:
    'Acá se configura todo lo necesario para que el sistema emita comprobantes fiscales reales ante ARCA: los datos del emisor, el certificado digital de cada ambiente, el ambiente activo (Simulado, Homologación o Producción) y los PV fiscales (ARCA) habilitados para facturar. No confundir con los "Puntos de venta" de la empresa (ver Configuración): un punto de venta se vincula a un PV fiscal y factura con su numeración. Solo el rol técnico `SUPER_ADMIN` accede a esta sección. Mientras no completes esta configuración y no pases a Producción, todas las facturas siguen emitiéndose en modo de prueba, sin validez fiscal.',
  steps: [
    {
      title: 'Completar los datos del emisor',
      description:
        'En la pestaña "Datos del emisor", cargá el CUIT, la razón social, el domicilio fiscal, la condición frente al IVA, Ingresos Brutos y la fecha de inicio de actividades. El sistema valida el dígito verificador del CUIT antes de guardar: si no coincide, rechaza el guardado con un mensaje específico y no persiste el cambio. Estos datos son la base del CSR y de cada comprobante que se emita.',
      keyTerms: ['condicion-iva-receptor'],
    },
    {
      title: 'Elegir el ambiente de trabajo',
      description:
        'En la pestaña "Ambiente", elegí entre Simulado, Homologación y Producción con los botones de radio. Simulado es el punto de partida: no hace ninguna llamada real. Homologación es el ambiente de pruebas de ARCA, con comprobantes que no tienen validez fiscal pero sí viajan por la red real. Producción emite comprobantes reales y exige haber completado el resto de esta guía.',
      keyTerms: ['homologacion'],
    },
    {
      title: 'Generar el pedido de certificado (CSR)',
      description:
        'En la pestaña "Certificados", en la tarjeta del ambiente (Homologación o Producción) completá el "Alias" y hacé clic en "Generar clave y CSR". El sistema genera localmente un par de claves y un pedido de certificado con tus datos de emisor. Si falta algún dato del emisor, el botón queda deshabilitado. Después hacé clic en "Descargar CSR": ese archivo es el que vas a presentar en ARCA en el paso siguiente.',
      keyTerms: ['pedido-de-certificado', 'certificado-digital'],
    },
    {
      title: 'Subirlo en ARCA y autorizar el servicio "wsfe"',
      description:
        'Con el pedido de certificado descargado, entrá al sitio de ARCA: usá WSASS para Homologación o el Administrador de Certificados Digitales para Producción. Presentá el pedido de certificado, autorizá el certificado resultante para el servicio "wsfe" y descargá el archivo `.crt` que te entrega ARCA.',
      keyTerms: ['pedido-de-certificado'],
    },
    {
      title: 'Cargar el certificado',
      description:
        'De vuelta en "Certificados", usá "Cargar certificado (.crt)" para subir el archivo que descargaste de ARCA para ese mismo ambiente. El sistema valida que corresponda a la clave generada en el paso anterior y que el CUIT del certificado coincida con el del emisor configurado; si alguna de las dos validaciones falla, rechaza la carga sin guardar nada. Si todo está bien, muestra el sujeto y la fecha de vencimiento del certificado, con un aviso si vence en menos de 30 días.',
      keyTerms: ['certificado-digital'],
    },
    {
      title: 'Probar la conexión',
      description:
        'En la pestaña "Probar conexión", hacé clic en "Probar conexión". El sistema hace, en orden, el inicio de sesión contra ARCA, una consulta de estado del servicio, la consulta de puntos de venta y la consulta de alícuotas de IVA, mostrando el resultado de cada uno. Si el inicio de sesión falla, los pasos siguientes se marcan como "No realizado" sin intentarse.',
      keyTerms: ['ticket-de-acceso'],
    },
    {
      title: 'Pasar a Producción',
      description:
        'Arriba de las pestañas, la tarjeta "Pasos para facturar con ARCA" muestra los 5 pasos numerados (Datos del emisor, Certificado de Homologación, Prueba de conexión en Homologación, Certificado de Producción, Prueba de conexión en Producción) con su progreso ("X de 5 listos"): cada paso pendiente tiene un botón "Continuar" que te lleva directo a esa pestaña. Seleccionar Producción abre un cuadro de confirmación que exige tipear el CUIT del emisor exactamente igual al configurado. El cambio solo se aplica si se cumplen, en ese momento, los 5 pasos de esa tarjeta más el CUIT tipeado coincidiendo. Si falta cualquiera de esas condiciones, el sistema rechaza el cambio e indica cuál falta.',
      keyTerms: [],
    },
    {
      title: 'Volver a un ambiente de prueba',
      description:
        'Desde Producción, podés volver a Homologación o Simulado en cualquier momento, de inmediato y sin ninguna de las cuatro condiciones del paso anterior: esa exigencia solo aplica para avanzar hacia Producción, nunca para retroceder.',
      keyTerms: [],
    },
    {
      title: 'Dar de alta un PV fiscal',
      description:
        'Andá a "PV fiscal (ARCA)" (accesible desde el mismo enlace al pie de la pestaña "Probar conexión", o desde la tarjeta "PV fiscal (ARCA)" en Configuración) y hacé clic en "Nuevo punto de venta". Cargá el número asignado por ARCA, una descripción y marcá qué tipos de comprobante (Factura A, B o C) emite ese PV fiscal. Después vinculalo al punto de venta que factura con él, desde Configuración → "Puntos de venta".',
      keyTerms: [],
    },
    {
      title: 'Activar o desactivar un PV fiscal',
      description:
        'En el listado de "PV fiscal (ARCA)", hacé clic en "Desactivar" o "Activar" sobre la fila correspondiente. Un PV fiscal inactivo no aparece disponible al generar facturas, pero conserva su numeración: el último número emitido por tipo de comprobante queda visible en la misma tabla.',
      keyTerms: [],
    },
  ],
  states: [],
  faq: [
    {
      question: '¿Por qué no veo estas tarjetas en Configuración?',
      answer:
        'Porque "Datos Fiscales y ARCA" y "PV fiscal (ARCA)" requieren el permiso "Configurar puntos de venta y credenciales fiscales", reservado al rol técnico `SUPER_ADMIN`. Ni Administración ni Facturación lo tienen: es una decisión deliberada, para que un único rol concentre el riesgo de esta configuración.',
    },
    {
      question:
        '¿Puedo generar el certificado de Producción sin haber probado antes en Homologación?',
      answer:
        'Podés generarlo, pero no vas a poder pasar a Producción sin una prueba de conexión exitosa en Producción hecha en las últimas 24 horas. En la práctica, conviene completar y probar Homologación primero.',
    },
    {
      question: '¿Qué pasa si mi certificado está por vencer?',
      answer:
        'El sistema muestra una advertencia visible cuando falta menos de 30 días para el vencimiento. Si ya venció, ese ambiente queda bloqueado para emitir hasta que subas un certificado nuevo; los demás ambientes no se ven afectados.',
    },
    {
      question: '¿La clave privada se puede ver o descargar después de generada?',
      answer:
        'No, en ningún caso. Se cifra al generarse y ninguna consulta, ni siquiera de `SUPER_ADMIN`, la devuelve en texto plano ni cifrada. Solo el pedido de certificado y, luego, el certificado público son descargables.',
    },
  ],
  tips: [
    'Guardá una copia del pedido de certificado y del `.crt` fuera del sistema: si perdés el certificado de un ambiente, tenés que generar un pedido nuevo y volver a autorizarlo en ARCA.',
    'Usá "Probar conexión" después de cada cambio de certificado, aunque la tarjeta "Pasos para facturar con ARCA" ya muestre 5 de 5 listos: confirma que ARCA acepta el certificado cargado antes de depender de él en una emisión real.',
  ],
};
